/**
 * LoginPage Component
 *
 * Login form. Tracks failed attempts to surface a "Send reset email" prompt
 * after 3 mistakes. Migration-pending accounts surface an inline "Activate"
 * card - no automatic emails are ever sent without explicit user consent.
 */

import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PublicLayout, Container } from '../../components/layout';
import { useAuth } from '../../contexts';
import { signInWithGoogle } from '../../services/auth';
import { PasswordInput } from './components';

const ATTEMPTS_BEFORE_RESET_PROMPT = 3;

export const LoginPage: React.FC = () => {
  const location = useLocation();
  const { login, user, resetPassword, sendActivationEmail } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // UX state for password-recovery prompts
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [emailActionLoading, setEmailActionLoading] = useState(false);
  const [emailActionSent, setEmailActionSent] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password, remember);
      toast.success('Welcome back!');
      // Reset recovery UI on success
      setFailedAttempts(0);
      setNeedsActivation(false);
      setEmailActionSent(false);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };

      if (authError.code === 'auth/needs-activation') {
        // Migration-pending: show inline activation card, don't toast.
        setNeedsActivation(true);
      } else if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
        toast.error('Incorrect email or password.');
        setFailedAttempts((n) => n + 1);
      } else if (authError.code === 'auth/user-not-found') {
        toast.error('No account found with this email.');
      } else if (authError.code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.');
      } else if (authError.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again in a few minutes.');
      } else if (authError.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection and try again.');
      } else if (authError.code === 'auth/user-disabled') {
        toast.error('This account has been disabled. Please contact support.');
      } else if (authError.code === 'auth/account-exists-with-different-credential') {
        toast.error(authError.message || 'Please use the original sign-in method for this account.');
      } else {
        toast.error("We couldn't sign you in. Please try again in a moment.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      setGoogleLoading(false);
      toast.error("We couldn't sign you in with Google. Please try again.");
    }
  };

  const handleSendResetEmail = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address first.');
      return;
    }
    setEmailActionLoading(true);
    try {
      await resetPassword(email);
      setEmailActionSent(true);
      toast.success('Reset link sent! Check your inbox.', { duration: 6000 });
    } catch (err: unknown) {
      const authErr = err as { code?: string };
      if (authErr.code === 'auth/user-not-found') {
        toast.error('No account found with this email.');
      } else {
        toast.error("We couldn't send a reset email right now. Please try again.");
      }
    } finally {
      setEmailActionLoading(false);
    }
  };

  const handleSendActivationEmail = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address first.');
      return;
    }
    setEmailActionLoading(true);
    try {
      await sendActivationEmail(email);
      setEmailActionSent(true);
      toast.success('Activation link sent! Check your inbox.', { duration: 6000 });
    } catch {
      toast.error("We couldn't send the activation email. Please try again.");
    } finally {
      setEmailActionLoading(false);
    }
  };

  // If user is already authenticated, redirect away from /login.
  if (user) {
    return <Navigate to={from} replace />;
  }

  const showResetPrompt = failedAttempts >= ATTEMPTS_BEFORE_RESET_PROMPT && !needsActivation;

  return (
    <PublicLayout hideFooter hideBottomNav>
      <Container className="min-h-screen flex items-center justify-center py-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-gray-500 mt-2">
              Log in to your DARAK account
            </p>
          </div>

          {/* Activation card (migration-pending) */}
          {needsActivation && (
            <div className="mb-6 p-5 bg-primary-50 border border-primary-200 rounded-2xl">
              <h3 className="font-semibold text-gray-900 mb-1">Activate your account</h3>
              <p className="text-sm text-gray-600 mb-4">
                We need to set up a password for this account. We'll email you a secure
                link to <strong className="break-all">{email}</strong>. Click the link
                in the email to create your password.
              </p>
              {emailActionSent ? (
                <p className="text-sm text-primary-700 font-medium">
                  ✓ Activation email sent. Please check your inbox.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleSendActivationEmail}
                  disabled={emailActionLoading}
                  className="w-full py-2.5 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  {emailActionLoading ? 'Sending…' : 'Send activation email'}
                </button>
              )}
            </div>
          )}

          {/* Reset prompt (after 3 failed attempts) */}
          {showResetPrompt && (
            <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-2xl">
              <h3 className="font-semibold text-gray-900 mb-1">Trouble signing in?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Keep trying, or we can email you a secure password reset link to{' '}
                <strong className="break-all">{email}</strong>.
              </p>
              {emailActionSent ? (
                <p className="text-sm text-amber-700 font-medium">
                  ✓ Reset email sent. Please check your inbox.
                </p>
              ) : (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSendResetEmail}
                    disabled={emailActionLoading}
                    className="flex-1 py-2.5 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {emailActionLoading ? 'Sending…' : 'Send reset email'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFailedAttempts(0)}
                    className="flex-1 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                  >
                    Keep trying
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Login Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 mb-6"
            >
              {googleLoading ? <LoadingSpinner /> : <GoogleIcon />}
              {googleLoading ? 'Signing in...' : 'Continue with Google'}
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    // Reset recovery prompts when user changes email
                    if (needsActivation) setNeedsActivation(false);
                    if (failedAttempts > 0) setFailedAttempts(0);
                    if (emailActionSent) setEmailActionSent(false);
                  }}
                  required
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <PasswordInput
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>

                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading || needsActivation}
                className="w-full py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingSpinner />
                    Logging in...
                  </span>
                ) : (
                  'Log in'
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </Container>
    </PublicLayout>
  );
};

const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default LoginPage;
